import _ from 'lodash'
import React from 'react'
import { Table } from 'semantic-ui-react'
import moment from 'moment'
import * as graph from '@adc/sdk'
import Text from '../../../elementals/text'
import Modal from '../../../overlays/modal'
import { formatMoney } from '../../../../utils/formatters'
import { isREO, getProductType } from './utils'
import ListingBadge, { BADGE_MODE } from './base'

/**
 * These formatters may be more specific, but they are used in the pdp exactly like this for now.
 *
 * TODO: Move this to shared toolkit formatter file, making sure these formats do not clash with
 * any other type formatters we already have.
 */
const formatDate = val => moment(val).format('MM/DD/YYYY')

/**
 * Lease Info
 */
const CURRENT_RENT = 'Current Rent'
const LEASE_START_DATE = 'Lease Start Date'
const LEASE_END_DATE = 'Lease End Date'
const REMAINING_TERM = 'Remaining Term'
const SECURITY_DEPOSIT = 'Security Deposit'
const SECURITY_DEPOSIT_AMOUNT = 'Security Deposit Amount'

/**
 * NOTE: This strategy only works, as all countdown algorithms work, if the units are in some defined strict size order.
 */
const calculateDateDiff = (
  start,
  end,
  periods = ['years', 'months', 'days']
) => {
  const parts = []
  let currentStart = start

  while (periods.length) {
    const [unit] = periods
    const amount = end.diff(currentStart, unit)

    if (amount) {
      currentStart = currentStart.add(amount, unit)
      parts.push(`${amount} ${unit}`)
    }

    periods.shift()
  }

  return parts.join(' ')
}

const LEASE_INFO = [
  {
    label: CURRENT_RENT,
    fieldPath: 'current_rent',
    elmId: 'current_rent',
    format: val => (val !== 'N/A' ? formatMoney(val) : val),
  },
  {
    label: LEASE_START_DATE,
    fieldPath: 'lease_start_date',
    elmId: 'lease_start_date',
    format: val => (val !== 'N/A' ? formatDate(val) : ' '),
  },
  {
    label: LEASE_END_DATE,
    fieldPath: 'lease_expiration_date',
    elmId: 'lease_expiration_date',
    format: val => (val !== 'N/A' ? formatDate(val) : ' '),
  },
  {
    label: REMAINING_TERM,
    fieldPath: 'lease_expiration_date',
    elmId: 'remaining_term',
    format: expiration =>
      expiration
        ? calculateDateDiff(moment(), moment(expiration))
        : 'Month-to-Month',
  },
  {
    label: SECURITY_DEPOSIT,
    fieldPath: 'is_security_deposit_charged',
    elmId: 'security_deposit_charged',
    format: val => (val ? 'Yes' : 'No'),
  },
  {
    label: SECURITY_DEPOSIT_AMOUNT,
    fieldPath: 'security_deposit_amount',
    elmId: 'security_deposit_amount',
    format: val => formatMoney(val),
  },
]

const getFieldValue = (property, fieldInfo = {}) => {
  const val = _.get(property, fieldInfo.fieldPath, '')
  return _.isFunction(fieldInfo.format) ? fieldInfo.format(val) : val
}

export default class RentalBadge extends ListingBadge {
  static title = 'Income Producing'

  static summary = 'Yes'

  static titleIcon = 'hands-usd'

  static shouldBeIncluded(listing, badgeMode) {
    // only applies to expanded, hide for condensed
    return (
      badgeMode === BADGE_MODE.EXPANDED &&
      getProductType(listing) === graph.enums.ProductType.Rentals &&
      isREO(listing)
    )
  }

  get content() {
    const { listing } = this.props

    return (
      <>
        This property has a tenant with a lease in place.{' '}
        <Modal
          title='Lease Information'
          size='tiny'
          mobileTitleSkin='light'
          fullScreen='mobileOnly'
          elmId='lease_info_modal'
          trigger={
            <a data-elm-id='lease_info_trigger'>See Lease Information</a>
          }
        >
          <Modal.Content data-elm-id='lease_info_content'>
            <Text>
              <div>
                This property has a tenant with a lease in place.{' '}
                <a
                  href='/lp/learn-more/rentals/'
                  data-elm-id='learn_more_rentals_link'
                  target='_blank'
                >
                  Learn more about Rentals
                </a>
              </div>
              <Table basic='very' compact='very' unstackable>
                <Table.Body>
                  {LEASE_INFO.map((fieldInfo, index) => {
                    const fieldValue = getFieldValue(listing.rental, fieldInfo)
                    return (
                      Boolean(fieldValue) && (
                        <Table.Row key={index}>
                          <Table.Cell>
                            <Text
                              color='gray'
                              weight='heavy'
                              data-elm-id={`${fieldInfo.elmId}_label`}
                            >
                              {fieldInfo.label}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text
                              color='gray'
                              data-elm-id={`${fieldInfo.elmId}_value`}
                            >
                              {fieldValue}
                            </Text>
                          </Table.Cell>
                        </Table.Row>
                      )
                    )
                  })}
                </Table.Body>
              </Table>
              <div>
                For specific lease terms, including the tenant's right to extend
                the lease, review the lease in the Additional Documents section.
              </div>
            </Text>
          </Modal.Content>
        </Modal>
      </>
    )
  }
}
